import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { HiCheck, HiOutlineFolder, HiOutlineInformationCircle, HiOutlineSearch } from 'react-icons/hi';
import { useDispatch } from "react-redux";
import { updateJiraExportStatus } from "../../../redux/slices/backgroundWorker";
import useJiraService from "../../../services/jira";
import CustomButton from '../../common/CustomButton';
import { Drawer } from "../index";

interface JiraProject {
    project_name: string;
    project_id: string;
}

interface JiraBoardResponse {
    project_name: string;
    active_board: string;
    available_boards: string[];
}

interface ExportToJiraModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentEntityId: string;
}

const ProjectItemSkeleton = () => (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 animate-pulse">
        <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700/50 rounded w-24"></div>
            </div>
        </div>
    </div>
);

const BoardItemSkeleton = () => (
    <div className="flex items-center gap-3 px-4 py-3 bg-white/40 dark:bg-gray-800/40 rounded-lg border border-gray-100 dark:border-gray-700 animate-pulse">
        <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
    </div>
);

const ExportToJiraModal = ({
    isOpen,
    onClose,
    currentEntityId
}: ExportToJiraModalProps) => {
    const dispatch = useDispatch();
    const { getJiraProjects, getJiraActiveBoard, exportProject } = useJiraService();
    const [projects, setProjects] = useState<JiraProject[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
    const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
    const [availableBoards, setAvailableBoards] = useState<string[]>([]);
    const [selectedBoard, setSelectedBoard] = useState<string | null>(null);
    const [isBoardsLoading, setIsBoardsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSelectedProjectId(null);
            setSelectedProjectName(null);
            setSelectedBoard(null);
            setAvailableBoards([]);
            setSearchQuery("");
            fetchProjects();
        }
    }, [isOpen]);

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const data = await getJiraProjects();
            setProjects(data.projects || []);
        } catch (error) {
            console.error("Failed to fetch Jira projects", error);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredProjects = useMemo(() => {
        if (!searchQuery.trim()) return projects;
        const query = searchQuery.toLowerCase();
        return projects.filter(p => 
            p.project_name.toLowerCase().includes(query) || 
            p.project_id.toLowerCase().includes(query)
        );
    }, [projects, searchQuery]);

    const fetchBoards = async (projectName: string) => {
        setIsBoardsLoading(true);
        try {
            const data: JiraBoardResponse = await getJiraActiveBoard(projectName);
            setAvailableBoards(data.available_boards || []);
            setSelectedBoard(data.active_board || null);
        } catch (error) {
            console.error("Failed to fetch Jira boards", error);
            setAvailableBoards([]);
            setSelectedBoard(null);
        } finally {
            setIsBoardsLoading(false);
        }
    };

    const handleProjectSelect = (project: JiraProject) => {
        if (selectedProjectId === project.project_id) return;
        
        setSelectedProjectId(project.project_id);
        setSelectedProjectName(project.project_name);
        setSelectedBoard(null);
        setAvailableBoards([]);
        fetchBoards(project.project_name);
    };

    const handleExport = async () => {
        if (!selectedProjectId) return;

        setIsExporting(true);
        try {
            await exportProject({
                entity_id: currentEntityId,
                jira_project_key: selectedProjectId,
                jira_project_name: selectedProjectName || "",
                active_board: selectedBoard || "",
                integration_type: "jira"
            });

            dispatch(updateJiraExportStatus({
                projectId: currentEntityId,
                status: {
                    is_completed: false,
                    status: 'loading'
                }
            }));

            toast.success("Project export initiated successfully!");
            onClose();
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Failed to initiate export");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            position="right"
        >
            <div className="h-full bg-gray-50 dark:bg-gray-900 flex flex-col">
                <div className="sticky top-0 z-20 shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                    <div className="px-6 py-5">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 group"
                            >
                                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex-1">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Export to Jira
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                    Select a Jira project to export your data
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 flex gap-3">
                        <HiOutlineInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                            This will export your project data to the selected Jira project. This process may take a few minutes.
                        </p>
                    </div>

                    <div className="relative">
                        <HiOutlineSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search Jira projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                        />
                    </div>

                    {isLoading ? (
                        <div className="space-y-3">
                            {[...Array(5)].map((_, i) => <ProjectItemSkeleton key={i} />)}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredProjects.map((project) => (
                                <div key={project.project_id} className="space-y-2">
                                    <div
                                        onClick={() => handleProjectSelect(project)}
                                        className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                                            selectedProjectId === project.project_id
                                                ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-400 dark:border-blue-500 shadow-sm shadow-blue-500/10'
                                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                                                selectedProjectId === project.project_id ? 'bg-blue-100 dark:bg-blue-800' : 'bg-gray-100 dark:bg-gray-700'
                                            }`}>
                                                <HiOutlineFolder className={selectedProjectId === project.project_id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'} />
                                            </div>
                                            <div className="min-w-0">
                                                <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                                    {project.project_name}
                                                </h4>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    ID: {project.project_id}
                                                </p>
                                            </div>
                                        </div>
                                        {selectedProjectId === project.project_id && (
                                            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                                <HiCheck className="text-white w-4 h-4" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Inline Board Selection */}
                                    {selectedProjectId === project.project_id && (
                                        <div className="ml-5 pl-5 border-l-2 border-blue-200 dark:border-blue-800 py-4 space-y-4 animate-fadeIn">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Available Boards
                                                </h3>
                                            </div>
                                            
                                            {isBoardsLoading ? (
                                                <div className="space-y-2">
                                                    {[...Array(3)].map((_, i) => <BoardItemSkeleton key={i} />)}
                                                </div>
                                            ) : availableBoards.length > 0 ? (
                                                <div className="grid grid-cols-1 gap-2">
                                                    {availableBoards.map((board) => (
                                                        <div
                                                            key={board}
                                                            onClick={() => setSelectedBoard(board)}
                                                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                                                                selectedBoard === board
                                                                    ? 'bg-white dark:bg-gray-800 border-blue-400 dark:border-blue-500 ring-1 ring-blue-400 dark:ring-blue-500 shadow-sm'
                                                                    : 'bg-white/50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800'
                                                            }`}
                                                        >
                                                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${
                                                                selectedBoard === board ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                                                            }`}>
                                                                {selectedBoard === board && <HiCheck className="text-white w-2.5 h-2.5" />}
                                                            </div>
                                                            <span className={`text-[13px] ${selectedBoard === board ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                {board}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg text-center border border-dashed border-gray-200 dark:border-gray-700">
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                        No boards found for this project.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {filteredProjects.length === 0 && !isLoading && (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No Jira projects found
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 left-0 right-0 shrink-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center z-10">
                    <CustomButton
                        onClick={onClose}
                        variant="cancel"
                        disabled={isExporting}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        onClick={handleExport}
                        variant="primary"
                        disabled={!selectedProjectId || !selectedBoard || isExporting}
                        isLoading={isExporting}
                    >
                        Export to Jira
                    </CustomButton>
                </div>
            </div>
        </Drawer>
    );
};

export default ExportToJiraModal;
